'use client';

import type { CSSProperties, ReactNode } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@akademi/ui';

// ---------------------------------------------------------------------------
// IneligibleInfo — the "why" behind a 9476 SHOW_BLOCKED card.
//
// The status chip and the disabled apply button can only say the generic
// "Şartları Karşılamıyorsunuz", which leaves the visitor guessing which rule
// they tripped. The backend already sends the answer: `eligibilityIssues[]`,
// localised, arrives on the same card payload the list renders. This badge
// puts an info affordance next to that label and opens the real reasons in a
// popover.
//
// Renders nothing when the payload carries no reason, so a tenant whose
// backend omits `eligibilityIssues` keeps exactly the card it has today.
// ---------------------------------------------------------------------------

export interface IneligibleInfoProps {
  /** Backend-localised reasons; blanks and duplicates are dropped here. */
  reasons?: string[];
  /** Popover heading, e.g. "Neden başvuramıyorum?". */
  title: string;
  /** Accessible name + native tooltip for the trigger. */
  triggerLabel: string;
  /** Trigger tint. Defaults to `currentColor` so it inherits the chip's tone. */
  color?: string;
}

export function IneligibleInfo({
  reasons,
  title,
  triggerLabel,
  color,
}: IneligibleInfoProps): ReactNode {
  const list = Array.from(
    new Set((reasons ?? []).map((r) => (typeof r === 'string' ? r.trim() : '')).filter(Boolean)),
  );
  if (list.length === 0) return null;

  return (
    <Popover>
      <PopoverTrigger
        type="button"
        aria-label={triggerLabel}
        title={triggerLabel}
        // The badge often sits inside a linked/clickable card; keep the click
        // on the popover instead of letting it navigate away from the answer.
        onClick={(e) => e.stopPropagation()}
        style={color ? { ...triggerStyle, color } : triggerStyle}
      >
        <IconInfo />
      </PopoverTrigger>
      <PopoverContent align="start" className="gap-2">
        <div style={titleStyle}>{title}</div>
        {list.length === 1 ? (
          <p style={reasonStyle}>{list[0]}</p>
        ) : (
          <ul style={listStyle}>
            {list.map((reason) => (
              <li key={reason} style={reasonStyle}>
                {reason}
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}

const triggerStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  border: 0,
  background: 'transparent',
  color: 'currentColor',
  cursor: 'pointer',
  lineHeight: 0,
  opacity: 0.85,
};

const titleStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm, 13px)',
  fontWeight: 700,
  color: 'var(--color-text-primary, #111827)',
};

const listStyle: CSSProperties = {
  margin: 0,
  paddingInlineStart: 18,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

const reasonStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-sm, 13px)',
  lineHeight: 1.45,
  color: 'var(--color-text-secondary, #4b5563)',
};

function IconInfo(): ReactNode {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      style={{ flexShrink: 0 }}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </svg>
  );
}
